/**
 * Opportunity Solution Tree Tool
 * 
 * Based on Teresa Torres' Continuous Discovery Habits framework.
 * Helps product teams visualize the path from desired outcomes to
 * validated solutions through:
 * 
 * 1. Outcome: Business goal/metric the team is trying to achieve
 * 2. Opportunities: Customer needs, pain points, and desires
 * 3. Solutions: Potential features/improvements to address opportunities
 * 4. Experiments: Tests to validate solution assumptions
 * 
 * Key principles:
 * - Start with a product outcome (not a business outcome)
 * - Opportunities come from customer interviews, not assumptions
 * - Focus on one target opportunity at a time
 * - Explore multiple solutions for each opportunity
 * - Test assumptions before building
 * 
 * MIGRATION NOTE (2026-01-22):
 * This component has been migrated from in-memory state to database-backed 
 * persistence using the ostStore. Per Build Contract section 2.1:
 * "All durable state must live in Supabase tables."
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Plus,
  Download,
  Upload,
  BookOpen,
  Target,
  Lightbulb,
  Wrench,
  FlaskConical,
  Info,
  FileText,
  FileJson,
  Copy,
  X,
  ExternalLink,
  ArrowRightCircle,
  Database,
  ChevronDown,
  ArrowLeft,
  Trash2,
} from 'lucide-react';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useOSTStore, buildCandidateFromSolution, type OSTNode as StoreOSTNode, type OSTTree } from '../../stores/prioritization/ostStore';
import { TreeVisualization } from './components/TreeVisualization';
import { NodeEditor } from './components/NodeEditor';
import { OstRootLinkModal, desiredOutcomeToRootFields } from './components/OstRootLinkModal';
import { useStrategyStore } from '../../stores/prioritization/strategyStore';
import type { StrategicObjective } from '../../lib/strategyAlignment';
import type { DesiredOutcome } from '../../stores/prioritization/desiredOutcomeStore';
import type { 
  OSTNode, 
  OpportunitySolutionTree as OSTType,
  NodeType,
  OutcomeNode,
  OpportunityNode,
  SolutionNode,
  ExperimentNode,
} from './types';
import { SAMPLE_TREE as sampleTreeData } from './types';
import { getSafeSupabaseClient, generateUUID, getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { StrategyConnectivityHelpButton } from '../prioritization/strategy/StrategyConnectivityDocModal';

// ============================================================================
// Helper Functions
// ============================================================================

function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Convert UI OSTNode type to store StoreOSTNode type
 * UI uses 'type', store uses 'nodeType'
 */
function uiNodeToStoreNode(uiNode: OSTNode): Partial<StoreOSTNode> {
  return {
    nodeType: uiNode.type,
    title: uiNode.title,
    description: uiNode.description,
    parentId: uiNode.parentId,
    position: uiNode.position || { x: 0, y: 0 },
    collapsed: uiNode.collapsed || false,
    // Outcome fields
    metric: (uiNode as OutcomeNode).metric,
    target: (uiNode as OutcomeNode).target,
    currentValue: (uiNode as OutcomeNode).currentValue,
    timeframe: (uiNode as OutcomeNode).timeframe,
    // Opportunity fields
    source: (uiNode as OpportunityNode).source,
    customerQuote: (uiNode as OpportunityNode).customerQuote,
    frequency: (uiNode as OpportunityNode).frequency,
    severity: (uiNode as OpportunityNode).severity,
    customerSegments: (uiNode as OpportunityNode).customerSegments,
    isTargeted: (uiNode as OpportunityNode).isTargeted,
    // Solution fields
    validationStatus: (uiNode as SolutionNode).validationStatus,
    effort: (uiNode as SolutionNode).effort,
    confidence: (uiNode as SolutionNode).confidence,
    assumptions: (uiNode as SolutionNode).assumptions,
    // Experiment fields
    experimentType: (uiNode as ExperimentNode).experimentType,
    experimentStatus: (uiNode as ExperimentNode).status,
    hypothesis: (uiNode as ExperimentNode).hypothesis,
    successCriteria: (uiNode as ExperimentNode).successCriteria,
    results: (uiNode as ExperimentNode).results,
    learnings: (uiNode as ExperimentNode).learnings,
  };
}

/**
 * Convert store StoreOSTNode type to UI OSTNode type
 * Store uses 'nodeType', UI uses 'type'
 */
function storeNodeToUiNode(storeNode: StoreOSTNode): OSTNode {
  const base = {
    id: storeNode.id,
    type: storeNode.nodeType as NodeType,
    title: storeNode.title,
    description: storeNode.description,
    createdAt: storeNode.createdAt.toISOString(),
    updatedAt: storeNode.updatedAt.toISOString(),
    parentId: storeNode.parentId,
    position: storeNode.position,
    collapsed: storeNode.collapsed,
    itemId: storeNode.itemId ?? null,
  };

  switch (storeNode.nodeType) {
    case 'outcome':
      return {
        ...base,
        type: 'outcome',
        parentId: null,
        metric: storeNode.metric,
        target: storeNode.target,
        currentValue: storeNode.currentValue,
        timeframe: storeNode.timeframe,
      } as OutcomeNode;
    case 'opportunity':
      return {
        ...base,
        type: 'opportunity',
        source: storeNode.source || 'customer_interview',
        customerQuote: storeNode.customerQuote,
        frequency: storeNode.frequency as OpportunityNode['frequency'],
        severity: storeNode.severity as OpportunityNode['severity'],
        customerSegments: storeNode.customerSegments,
        isTargeted: storeNode.isTargeted,
      } as OpportunityNode;
    case 'solution':
      return {
        ...base,
        type: 'solution',
        validationStatus: storeNode.validationStatus || 'not_started',
        effort: storeNode.effort as SolutionNode['effort'],
        confidence: storeNode.confidence,
        assumptions: storeNode.assumptions,
      } as SolutionNode;
    case 'experiment':
      return {
        ...base,
        type: 'experiment',
        experimentType: storeNode.experimentType || 'prototype_test',
        status: storeNode.experimentStatus || 'planned',
        hypothesis: storeNode.hypothesis,
        successCriteria: storeNode.successCriteria,
        results: storeNode.results,
        learnings: storeNode.learnings,
        startDate: storeNode.startDate?.toISOString(),
        endDate: storeNode.endDate?.toISOString(),
      } as ExperimentNode;
    default:
      return base as OSTNode;
  }
}

function createEmptyNode(type: NodeType, parentId: string | null): OSTNode {
  const base = {
    id: generateId(type),
    type,
    title: '',
    description: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    parentId,
  };
  
  switch (type) {
    case 'outcome':
      return { ...base, type: 'outcome', parentId: null } as OutcomeNode;
    case 'opportunity':
      return { ...base, type: 'opportunity', source: 'customer_interview', parentId: parentId! } as OpportunityNode;
    case 'solution':
      return { ...base, type: 'solution', validationStatus: 'not_started', parentId: parentId! } as SolutionNode;
    case 'experiment':
      return { ...base, type: 'experiment', experimentType: 'prototype_test', status: 'planned', parentId: parentId! } as ExperimentNode;
  }
}

// ============================================================================
// Stats Component
// ============================================================================

function TreeStats({ nodes }: { nodes: OSTNode[] }) {
  const stats = useMemo(() => {
    const outcomes = nodes.filter(n => n.type === 'outcome').length;
    const opportunities = nodes.filter(n => n.type === 'opportunity').length;
    const solutions = nodes.filter(n => n.type === 'solution').length;
    const experiments = nodes.filter(n => n.type === 'experiment').length;
    const validated = nodes.filter(n => n.type === 'solution' && (n as SolutionNode).validationStatus === 'validated').length;
    const targetOpportunity = nodes.find(n => n.type === 'opportunity' && (n as OpportunityNode).isTargeted);
    
    return { outcomes, opportunities, solutions, experiments, validated, targetOpportunity };
  }, [nodes]);
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-3">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="text-sm font-medium text-[var(--text-sec)]">Outcomes</span>
        </div>
        <p className="text-2xl font-bold text-[var(--text-sec)] mt-1">{stats.outcomes}</p>
      </div>
      
      <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-[var(--mustard)]" />
          <span className="text-sm font-medium text-[var(--mustard)]">Opportunities</span>
        </div>
        <p className="text-2xl font-bold text-[var(--mustard)] mt-1">{stats.opportunities}</p>
      </div>
      
      <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-3">
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="text-sm font-medium text-[var(--text-sec)]">Solutions</span>
        </div>
        <p className="text-2xl font-bold text-[var(--text-sec)] mt-1">
          {stats.solutions}
          {stats.validated > 0 && (
            <span className="text-sm font-normal ml-1 text-[var(--green)] dark:text-[var(--text-ter)]">
              ({stats.validated} validated)
            </span>
          )}
        </p>
      </div>
      
      <div className="bg-[var(--green-dim)] border border-[var(--green)] p-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-[var(--green)]" />
          <span className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Experiments</span>
        </div>
        <p className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)] mt-1">{stats.experiments}</p>
      </div>
    </div>
  );
}

// ============================================================================
// Getting Started Guide
// ============================================================================

function GettingStartedGuide({ onClose, onLoadSample }: { onClose: () => void; onLoadSample: () => void }) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-consulting-slate-blue-100">
            <BookOpen className="h-5 w-5 text-consulting-slate-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-[var(--text-pri)]">Getting Started with Strategies</h3>
            <p className="text-sm text-[var(--text-sec)]">Outcome → Opportunities → Solutions → Experiments</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <h4 className="font-medium text-[var(--text-pri)]">Before You Start</h4>
          <ul className="text-sm text-[var(--text-sec)] space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-consulting-slate-blue-500 font-bold">1.</span>
              Have a clear product outcome (not just a business metric)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-consulting-slate-blue-500 font-bold">2.</span>
              Conduct 3-4 story-based customer interviews first
            </li>
            <li className="flex items-start gap-2">
              <span className="text-consulting-slate-blue-500 font-bold">3.</span>
              Know your target customer and value proposition
            </li>
          </ul>
        </div>
        
        <div className="space-y-3">
          <h4 className="font-medium text-[var(--text-pri)]">How It Works</h4>
          <ul className="text-sm text-[var(--text-sec)] space-y-2">
            <li className="flex items-start gap-2">
              <Target className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
              <span><strong>Outcome:</strong> The metric you're trying to move</span>
            </li>
            <li className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
              <span><strong>Opportunities:</strong> Customer needs from interviews</span>
            </li>
            <li className="flex items-start gap-2">
              <Wrench className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
              <span><strong>Solutions:</strong> Ideas to address opportunities</span>
            </li>
            <li className="flex items-start gap-2">
              <FlaskConical className="h-4 w-4 text-[var(--green)] flex-shrink-0 mt-0.5" />
              <span><strong>Experiments:</strong> Tests to validate solutions</span>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={onLoadSample}
          className="px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors text-sm font-medium"
        >
          Load Example Tree
        </button>
        <a
          href="https://www.producttalk.org/opportunity-solution-tree/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] text-sm"
        >
          Learn more
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function OpportunitySolutionTree() {
  const { treeId: urlTreeId } = useParams<{ treeId: string }>();
  const navigate = useNavigate();

  // ========================================================================
  // Database-backed state from ostStore
  // Per Build Contract 2.1: "All durable state must live in Supabase tables."
  // ========================================================================
  
  // Store state
  const storeTrees = useOSTStore(state => state.trees);
  const storeNodes = useOSTStore(state => state.nodes);
  const activeTreeId = useOSTStore(state => state.activeTreeId);
  const isLoading = useOSTStore(state => state.isLoading);
  const storeError = useOSTStore(state => state.error);
  
  // Store actions
  const fetchTrees = useOSTStore(state => state.fetchTrees);
  const fetchTree = useOSTStore(state => state.fetchTree);
  const fetchNodes = useOSTStore(state => state.fetchNodes);
  const createTree = useOSTStore(state => state.createTree);
  const updateTree = useOSTStore(state => state.updateTree);
  const deleteTree = useOSTStore(state => state.deleteTree);
  const setActiveTree = useOSTStore(state => state.setActiveTree);
  const createNode = useOSTStore(state => state.createNode);
  const updateNode = useOSTStore(state => state.updateNode);
  const deleteNode = useOSTStore(state => state.deleteNode);
  const setTargetedOpportunity = useOSTStore(state => state.setTargetedOpportunity);
  const attachItemToNode = useOSTStore(state => state.attachItemToNode);
  const detachItemFromNode = useOSTStore(state => state.detachItemFromNode);
  
  // UI state (ephemeral - OK in memory per Build Contract 2.2)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [showImportToCycleModal, setShowImportToCycleModal] = useState(false);
  const [showTreeSelector, setShowTreeSelector] = useState(false);
  const [rootLinkModal, setRootLinkModal] = useState<
    null | { mode: 'objective' | 'desired-outcome'; outcomeNodeId: string }
  >(null);
  const [isAddingNode, setIsAddingNode] = useState(false);
  const [addNodeError, setAddNodeError] = useState<string | null>(null);
  
  // Get cycles and item-store helpers for the import feature.
  const cycles = useDegapProcessStore(state => state.cycles);
  const addCandidate = useItemsStore(state => state.addCandidate);
  const addItemsToCycle = useDegapProcessStore(state => state.addItemsToCycle);
  const userId = useUserStore(state => state.userId);
  const workspaceId = useTenantStore(state => state.currentWorkspaceId);
  const strategies = useStrategyStore((state) => state.strategies);
  const fetchStrategies = useStrategyStore((state) => state.fetchFromSupabase);
  
  // ========================================================================
  // Load trees on mount
  // ========================================================================
  useEffect(() => {
    fetchTrees(workspaceId || undefined);
  }, [fetchTrees, workspaceId]);

  // Sync URL treeId to active tree (list → detail pattern)
  useEffect(() => {
    if (!urlTreeId) return;
    setActiveTree(urlTreeId);
  }, [urlTreeId, setActiveTree]);

  // When trees have loaded, if URL has treeId but tree not in list, try fetch by id then redirect to list if missing
  useEffect(() => {
    if (!urlTreeId || isLoading) return;
    const inList = storeTrees.some(t => t.id === urlTreeId);
    if (inList) return;
    let cancelled = false;
    fetchTree(urlTreeId).then((tree) => {
      if (cancelled) return;
      if (!tree) navigate('/prioritization/strategy', { replace: true });
    });
    return () => { cancelled = true; };
  }, [urlTreeId, isLoading, storeTrees, fetchTree, navigate]);
  
  // Load nodes when active tree changes
  useEffect(() => {
    if (activeTreeId) {
      fetchNodes(activeTreeId);
      setShowGuide(false);
    }
  }, [activeTreeId, fetchNodes]);
  
  // ========================================================================
  // Derived state from store - convert to UI types
  // ========================================================================
  
  // Active tree
  const activeTree = useMemo(() => 
    activeTreeId ? storeTrees.find(t => t.id === activeTreeId) : null,
    [storeTrees, activeTreeId]
  );

  const canonicalStrategy = useMemo(() => {
    if (!activeTree?.strategyId) return null;
    return strategies[activeTree.strategyId] ?? null;
  }, [activeTree?.strategyId, strategies]);

  useEffect(() => {
    if (activeTree?.strategyId) fetchStrategies();
  }, [activeTree?.strategyId, fetchStrategies]);
  
  // Convert store nodes to UI nodes for the active tree
  const uiNodes = useMemo(() => {
    if (!activeTreeId) return [];
    const treeNodes = storeNodes.filter(n => n.treeId === activeTreeId);
    return treeNodes.map(storeNodeToUiNode);
  }, [storeNodes, activeTreeId]);
  
  // Build tree object for legacy components
  const tree = useMemo((): OSTType => {
    if (!activeTree) {
      return {
        id: 'empty',
        name: 'New Strategy',
        description: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        nodes: [],
      };
    }
    return {
      id: activeTree.id,
      name: activeTree.name,
      description: activeTree.description,
      createdAt: activeTree.createdAt.toISOString(),
      updatedAt: activeTree.updatedAt.toISOString(),
      nodes: uiNodes,
      metadata: {
        teamName: activeTree.teamName,
        productArea: activeTree.productArea,
        quarter: activeTree.quarter,
        interviewCount: activeTree.interviewCount,
        lastInterviewDate: activeTree.lastInterviewDate?.toISOString(),
      },
    };
  }, [activeTree, uiNodes]);
  
  // Get solution nodes for import
  const solutionNodes = useMemo(
    () => tree.nodes.filter(n => n.type === 'solution') as SolutionNode[],
    [tree.nodes]
  );
  
  const selectedNode = useMemo(
    () => tree.nodes.find(n => n.id === selectedNodeId) || null,
    [tree.nodes, selectedNodeId]
  );
  
  // ========================================================================
  // Handlers - now write to database through store
  // ========================================================================
  
  const handleCreateTree = useCallback(() => {
    navigate('/prioritization/strategy/new?framework=ost');
  }, [navigate]);
  
  const handleSelectTree = useCallback((treeId: string) => {
    setActiveTree(treeId);
    setShowTreeSelector(false);
    navigate(`/prioritization/strategy/opportunity-tree/${treeId}`);
  }, [setActiveTree, navigate]);
  
  const handleAddNode = useCallback(async (parentId: string | null, type: NodeType) => {
    if (isAddingNode) return;
    setAddNodeError(null);
    setIsAddingNode(true);

    try {
      if (!activeTreeId) {
        await handleCreateTree();
        return;
      }

      const newNode = await createNode({
        treeId: activeTreeId,
        nodeType: type,
        title: '',
        description: '',
        parentId: parentId || undefined,
        createdBy: userId || undefined,
        ...(type === 'opportunity' && { source: 'customer_interview' }),
        ...(type === 'solution' && { validationStatus: 'not_started' }),
        ...(type === 'experiment' && { experimentType: 'prototype_test', experimentStatus: 'planned' }),
      });

      if (newNode) {
        setSelectedNodeId(newNode.id);
        setShowEditor(true);
      } else {
        setAddNodeError('Failed to create node. Please check your connection and try again.');
      }
    } finally {
      setIsAddingNode(false);
    }
  }, [activeTreeId, createNode, userId, handleCreateTree, isAddingNode]);
  
  const handleSelectNode = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
    setShowEditor(true);
  }, []);
  
  const handleSaveNode = useCallback(async (updatedNode: OSTNode) => {
    const storeUpdates = uiNodeToStoreNode(updatedNode);
    await updateNode(updatedNode.id, storeUpdates);
  }, [updateNode]);
  
  const handleDeleteNode = useCallback(async (nodeId: string) => {
    await deleteNode(nodeId);
    setSelectedNodeId(null);
    setShowEditor(false);
  }, [deleteNode]);
  
  const handleToggleCollapse = useCallback(async (nodeId: string) => {
    const node = storeNodes.find(n => n.id === nodeId);
    if (node) {
      await updateNode(nodeId, { collapsed: !node.collapsed });
    }
  }, [storeNodes, updateNode]);
  
  const handleSetTargetOpportunity = useCallback(async (nodeId: string) => {
    await setTargetedOpportunity(nodeId);
  }, [setTargetedOpportunity]);

  const handleAttachItem = useCallback(async (nodeId: string, itemId: string) => {
    await attachItemToNode(nodeId, itemId);
  }, [attachItemToNode]);

  const handleDetachItem = useCallback(async (nodeId: string) => {
    await detachItemFromNode(nodeId);
  }, [detachItemFromNode]);

  const handlePickStrategicObjective = useCallback((outcomeNodeId: string) => {
    setRootLinkModal({ mode: 'objective', outcomeNodeId });
  }, []);

  const handlePickDesiredOutcome = useCallback((outcomeNodeId: string) => {
    setRootLinkModal({ mode: 'desired-outcome', outcomeNodeId });
  }, []);

  const applyObjectiveToStrategyNode = useCallback(
    async (outcomeNodeId: string, objective: StrategicObjective) => {
      await updateNode(outcomeNodeId, {
        title: objective.name,
        description: objective.description || undefined,
      });
      setRootLinkModal(null);
    },
    [updateNode]
  );

  const applyDesiredOutcomeToStrategyNode = useCallback(
    async (outcomeNodeId: string, outcome: DesiredOutcome) => {
      const { title, description } = desiredOutcomeToRootFields(outcome);
      await updateNode(outcomeNodeId, { title, description });
      setRootLinkModal(null);
    },
    [updateNode]
  );
  
  const handleReset = useCallback(async () => {
    if (!activeTreeId) return;
    
    if (tree.nodes.length === 0 || window.confirm('Are you sure you want to delete this tree? This cannot be undone.')) {
      await deleteTree(activeTreeId);
      setSelectedNodeId(null);
      setShowEditor(false);
      setShowGuide(true);
      navigate('/prioritization/strategy');
    }
  }, [activeTreeId, tree.nodes.length, deleteTree, navigate]);
  
  // Import solutions to a prioritization cycle as items (with governance events)
  const handleImportToCycle = useCallback(async (cycleId: string) => {
    if (solutionNodes.length === 0) {
      alert('No solutions to import. Add some solutions to your tree first.');
      return;
    }
    
    const cycleName = cycles.find(c => c.id === cycleId)?.name || 'cycle';
    const itemIds: string[] = [];
    const importedSolutions: Array<{ solutionId: string; opportunityId: string; candidateId: string }> = [];
    
    // Find store versions of nodes for proper linking
    const storeSolutions = storeNodes.filter(n => n.nodeType === 'solution' && n.treeId === activeTreeId);
    
    // Create an item for each solution.
    for (const storeSolution of storeSolutions) {
      // Find parent opportunity
      const opportunity = storeNodes.find(n => n.id === storeSolution.parentId);
      if (!opportunity) continue;
      
      // Find outcome
      const outcome = storeNodes.find(n => n.treeId === activeTreeId && n.nodeType === 'outcome' && n.parentId === null);
      if (!outcome) continue;
      
      // Build the imported item payload with OST linkage intact.
      const candidateData = buildCandidateFromSolution({
        solution: storeSolution,
        opportunity,
        outcome,
        cycleId,
        createdBy: userId || 'system',
      });
      
      const candidate = addCandidate({
        ...candidateData,
        submittedBy: userId || 'system',
      } as any);
      
      if (candidate?.id) {
        itemIds.push(candidate.id);
        importedSolutions.push({
          solutionId: storeSolution.id,
          opportunityId: opportunity.id,
          candidateId: candidate.id,
        });
      }
    }
    
    // Add imported items to the cycle.
    if (itemIds.length > 0) {
      await addItemsToCycle(cycleId, itemIds);
      
      // Emit governance event (per Doc 05)
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        try {
          await supabase.from('governance_events').insert({
            id: generateUUID(),
            cycle_id: cycleId,
            workspace_id: getCurrentWorkspaceId() ?? null,
            event_type: 'candidates_imported_from_ost',
            actor_id: userId,
            occurred_at: new Date().toISOString(),
            note: JSON.stringify({
              source: 'opportunity_solution_tree',
              treeId: activeTreeId,
              treeName: activeTree?.name,
              itemCount: itemIds.length,
              itemIds,
              solutions: importedSolutions,
            }),
          });
        } catch (govErr) {
          console.warn('[OST] Governance event failed (non-blocking):', govErr);
        }
      }
    }
    
    setShowImportToCycleModal(false);
    alert(`Imported ${itemIds.length} solutions as items to ${cycleName}`);
  }, [solutionNodes, storeNodes, activeTreeId, activeTree, cycles, addCandidate, addItemsToCycle, userId]);
  
  const handleLoadSample = useCallback(async () => {
    // Create a new tree with sample data
    const newTree = await createTree({
      name: sampleTreeData.name,
      description: sampleTreeData.description,
      workspaceId: workspaceId || undefined,
      ownerId: userId || undefined,
      metadata: sampleTreeData.metadata,
    });
    
    if (newTree) {
      // Create all sample nodes
      for (const uiNode of sampleTreeData.nodes) {
        await createNode({
          treeId: newTree.id,
          nodeType: uiNode.type,
          title: uiNode.title,
          description: uiNode.description,
          parentId: uiNode.parentId || undefined,
          createdBy: userId || undefined,
          ...uiNodeToStoreNode(uiNode),
        });
      }
      
      setActiveTree(newTree.id);
      setShowGuide(false);
      setSelectedNodeId(null);
      setShowEditor(false);
    }
  }, [createTree, createNode, setActiveTree, workspaceId, userId]);
  
  const handleExport = useCallback((format: 'json' | 'markdown' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';
    
    switch (format) {
      case 'json':
        content = JSON.stringify({ version: '1.0', exportedAt: new Date().toISOString(), tree }, null, 2);
        filename = `ost-${tree.name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      
      case 'markdown': {
        content = `# Strategy: ${tree.name}\n\n`;
        content += `> ${tree.description || 'No description'}\n\n`;
        content += `Generated: ${new Date().toLocaleString()}\n\n`;
        
        // Find outcomes and render tree
        const outcomes = tree.nodes.filter(n => n.type === 'outcome');
        
        function renderNode(node: OSTNode, indent: string = '') {
          let md = `${indent}- **${node.title}**`;
          if (node.description) md += `: ${node.description}`;
          md += '\n';
          
          const children = tree.nodes.filter(n => n.parentId === node.id);
          children.forEach(child => {
            md += renderNode(child, indent + '  ');
          });
          
          return md;
        }
        
        outcomes.forEach(outcome => {
          content += `## Outcome\n${renderNode(outcome)}\n`;
        });
        
        filename = `ost-${tree.name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      }
      
      case 'clipboard': {
        const summary = tree.nodes.map(n => `[${n.type.toUpperCase()}] ${n.title}`).join('\n');
        navigator.clipboard.writeText(summary);
        alert('Copied tree summary to clipboard!');
        return;
      }
    }
    
    if (format !== 'clipboard') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [tree]);
  
  const handleImport = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        
        if (data.tree && data.tree.nodes) {
          // Create a new tree from the imported data
          const importedTree = data.tree as OSTType;
          const newTree = await createTree({
            name: importedTree.name,
            description: importedTree.description,
            workspaceId: workspaceId || undefined,
            ownerId: userId || undefined,
            metadata: importedTree.metadata,
          });
          
          if (newTree) {
            // Create all imported nodes
            for (const uiNode of importedTree.nodes) {
              await createNode({
                treeId: newTree.id,
                nodeType: uiNode.type,
                title: uiNode.title,
                description: uiNode.description,
                parentId: uiNode.parentId || undefined,
                createdBy: userId || undefined,
                ...uiNodeToStoreNode(uiNode),
              });
            }
            
            setActiveTree(newTree.id);
            setShowGuide(false);
            setSelectedNodeId(null);
            setShowEditor(false);
          }
        } else {
          throw new Error('Invalid file format');
        }
      } catch (error) {
        alert('Failed to import file. Please ensure it is a valid OST JSON export.');
      }
    };
    input.click();
  }, [createTree, createNode, setActiveTree, workspaceId, userId]);
  

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      {/* Back nav */}
      <div className="mb-4">
        <Link
          to="/prioritization/strategy"
          className="inline-flex items-center text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-[var(--text-sec)]"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Strategies
        </Link>
      </div>

      {/* Page header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 max-w-3xl">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {canonicalStrategy?.name ?? activeTree?.name ?? 'Opportunity Solution Tree'}
            </h1>
            {activeTreeId && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-consulting-slate-blue-50 text-consulting-slate-blue-800 dark:bg-consulting-slate-blue-900/30 dark:text-consulting-slate-blue-300 text-xs font-medium rounded-full border border-consulting-slate-blue-100 dark:border-consulting-slate-blue-800">
                <Database className="h-3 w-3" />
                Saved
              </span>
            )}
          </div>
          {(canonicalStrategy?.description || activeTree?.description) && (
            <p className="mt-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
              {canonicalStrategy?.description || activeTree?.description}
            </p>
          )}
          <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Opportunities → solutions → experiments beneath your strategy
          </p>
        </div>

        <div className="flex items-center gap-2">
          <StrategyConnectivityHelpButton context="ost_editor" />
          {/* Tree switcher */}
          <div className="relative">
            <button
              onClick={() => setShowTreeSelector(!showTreeSelector)}
              className="flex items-center gap-2 px-3 py-2 border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]"
            >
              <Target className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="max-w-[150px] truncate">
                {activeTree?.name || 'Select tree'}
              </span>
              <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
            </button>

            {showTreeSelector && (
              <div className="absolute top-full right-0 mt-1 w-64 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] shadow-lg z-50">
                <div className="max-h-64 overflow-y-auto divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                  {storeTrees.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-[var(--text-ter)] text-center">
                      No trees yet.
                    </p>
                  ) : (
                    storeTrees.map(t => (
                      <button
                        key={t.id}
                        onClick={() => handleSelectTree(t.id)}
                        className={`w-full text-left px-3 py-2.5 text-sm hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors ${
                          t.id === activeTreeId
                            ? 'text-consulting-slate-blue-700 dark:text-consulting-slate-blue-400 bg-consulting-slate-blue-50 dark:bg-consulting-slate-blue-900/20'
                            : 'text-[var(--text-sec)] dark:text-[var(--text-pri)]'
                        }`}
                      >
                        <div className="font-medium truncate">{t.name}</div>
                        {t.productArea && (
                          <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{t.productArea}</div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Always-visible New Tree button */}
          <button
            type="button"
            onClick={handleCreateTree}
            className="flex items-center gap-2 px-3 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Tree
          </button>

          {/* Delete current tree */}
          {activeTreeId && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-2 px-3 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 hover:border-[var(--coral)] dark:hover:border-[var(--coral)] transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete Tree
            </button>
          )}

          {/* Export tools */}
          <div className="flex items-center border border-[var(--rule)] overflow-hidden">
            <button onClick={() => handleExport('markdown')} className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors" title="Export as Markdown">
              <FileText className="h-4 w-4" />
            </button>
            <button onClick={() => handleExport('json')} className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors border-l border-[var(--rule)]" title="Export as JSON">
              <FileJson className="h-4 w-4" />
            </button>
            <button onClick={() => handleExport('clipboard')} className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors border-l border-[var(--rule)]" title="Copy to Clipboard">
              <Copy className="h-4 w-4" />
            </button>
            <button onClick={handleImport} className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors border-l border-[var(--rule)]" title="Import">
              <Upload className="h-4 w-4" />
            </button>
          </div>

          {solutionNodes.length > 0 && (
            <button
              onClick={() => setShowImportToCycleModal(true)}
              className="flex items-center gap-2 px-3 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors text-sm font-medium"
            >
              <ArrowRightCircle className="h-4 w-4" />
              Import to Cycle
            </button>
          )}
        </div>
      </div>


      <div className="flex flex-col gap-6">
      
      {/* Main content */}
      <div className="flex gap-6 items-start">
        {/* Tree area */}
        <div className="flex-1 min-w-0">
          {showGuide && tree.nodes.length === 0 && (
            <div className="mb-6">
              <GettingStartedGuide
                onClose={() => setShowGuide(false)}
                onLoadSample={handleLoadSample}
              />
            </div>
          )}

          {tree.nodes.length > 0 && (
            <div className="mb-6">
              <TreeStats nodes={tree.nodes} />
            </div>
          )}

          <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] min-h-[400px]">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin h-8 w-8 border-2 border-consulting-slate-blue-500 border-t-transparent rounded-full mb-4" />
                <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">Loading…</p>
              </div>
            ) : !activeTreeId ? (
              /* ── No tree selected ── */
              <div className="flex flex-col items-center justify-center py-20 px-6">
                <Target className="h-12 w-12 text-[var(--text-ter)] mb-4" />
                <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">
                  No tree selected
                </h3>
                <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] text-center mb-6 max-w-sm">
                  Select an existing tree from the dropdown or create a new one.
                </p>
                <button
                  type="button"
                  onClick={handleCreateTree}
                  className="flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors text-sm font-medium"
                >
                  <Plus className="h-4 w-4" />
                  Create New Tree
                </button>
              </div>
            ) : tree.nodes.length === 0 ? (
              activeTree?.strategyId ? (
                <div className="flex flex-col items-center justify-center py-20 px-6">
                  <div className="animate-spin h-8 w-8 border-2 border-consulting-slate-blue-500 border-t-transparent rounded-full mb-4" />
                  <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] text-center">
                    Preparing your strategy node…
                  </p>
                </div>
              ) : (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">
                  Add a strategy root
                </h3>
                <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] text-center mb-6 max-w-md">
                  Start with a strategy node, then use the + menu on that row to add opportunities or pull in a strategic objective or desired outcome.
                </p>
                <button
                  type="button"
                  onClick={() => handleAddNode(null, 'outcome')}
                  disabled={isAddingNode}
                  className="flex items-center gap-2 px-4 py-2.5 bg-consulting-slate-blue-600 text-white text-sm font-medium hover:bg-consulting-slate-blue-700 transition-colors disabled:opacity-60"
                >
                  <Plus className="h-4 w-4" />
                  {isAddingNode ? 'Adding…' : 'Add strategy root'}
                </button>
                {addNodeError && (
                  <p className="mt-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]">{addNodeError}</p>
                )}
              </div>
              )
            ) : (
              <TreeVisualization
                nodes={tree.nodes}
                selectedNodeId={selectedNodeId}
                onSelectNode={handleSelectNode}
                onAddChild={handleAddNode}
                onToggleCollapse={handleToggleCollapse}
                onPickStrategicObjective={handlePickStrategicObjective}
                onPickDesiredOutcome={handlePickDesiredOutcome}
              />
            )}
          </div>

          {tree.nodes.length > 0 && (
            <div className="mt-6 bg-consulting-slate-blue-50/80 dark:bg-consulting-slate-blue-900/15 border border-consulting-slate-blue-100 dark:border-consulting-slate-blue-800 p-4">
              <div className="flex items-start gap-3">
                <Info className="h-4 w-4 text-consulting-slate-blue-700 dark:text-consulting-slate-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-[var(--text-pri)] dark:text-[var(--text-sec)]">
                  <p className="font-medium mb-1 text-consulting-slate-blue-900 dark:text-consulting-slate-blue-200">Tips for effective discovery</p>
                  <ul className="list-disc list-inside space-y-1 text-xs text-[var(--text-sec)] dark:text-[var(--text-ter)]">
                    <li>Focus on one target opportunity at a time</li>
                    <li>Generate at least 3 solutions for your target opportunity</li>
                    <li>Test assumptions before committing to a solution</li>
                    <li>Update your tree after every 3–4 customer interviews</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Editor panel */}
        {showEditor && (
          <div className="w-96 flex-shrink-0">
            <NodeEditor
              node={selectedNode}
              onSave={handleSaveNode}
              onDelete={handleDeleteNode}
              onClose={() => {
                setShowEditor(false);
                setSelectedNodeId(null);
              }}
              onSetTargetOpportunity={handleSetTargetOpportunity}
              onAttachItem={handleAttachItem}
              onDetachItem={handleDetachItem}
            />
          </div>
        )}
      </div>

      </div>{/* end flex flex-col gap-6 */}

      {rootLinkModal && (
        <OstRootLinkModal
          mode={rootLinkModal.mode}
          outcomeNodeId={rootLinkModal.outcomeNodeId}
          workspaceId={workspaceId}
          strategyId={activeTree?.strategyId ?? undefined}
          userId={userId}
          onClose={() => setRootLinkModal(null)}
          onApplyObjective={applyObjectiveToStrategyNode}
          onApplyDesiredOutcome={applyDesiredOutcomeToStrategyNode}
        />
      )}

      {/* Import to Cycle Modal */}
      {showImportToCycleModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75" onClick={() => setShowImportToCycleModal(false)} />
            <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-[var(--text-pri)]">Import Solutions to Cycle</h3>
                <button onClick={() => setShowImportToCycleModal(false)} className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <p className="text-sm text-[var(--text-sec)] mb-4">
                Import {solutionNodes.length} solution{solutionNodes.length !== 1 ? 's' : ''} as items to a prioritization cycle.
              </p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {cycles.length === 0 ? (
                  <p className="text-sm text-[var(--text-ter)] py-4 text-center">No cycles available. Create a cycle first.</p>
                ) : (
                  cycles.map((cycle) => (
                    <button
                      key={cycle.id}
                      onClick={() => handleImportToCycle(cycle.id)}
                      className="w-full text-left px-4 py-3 border border-[var(--rule)] hover:border-[var(--green)] hover:bg-[var(--green-dim)] transition-colors"
                    >
                      <p className="text-sm font-medium text-[var(--text-pri)]">{cycle.name}</p>
                      <p className="text-xs text-[var(--text-ter)]">
                        Phase: {cycle.currentPhase} | {cycle.itemIds?.length || 0} items
                      </p>
                    </button>
                  ))
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowImportToCycleModal(false)}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
